import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Timer } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import { setQueryDataMapped } from '../../lib/queryClient';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { Switch } from '../../components/ui/switch';
import type { ClockRoundingSetting } from '../../types/clock-rounding';


export const CLOCK_ROUNDING_QUERY_KEY = ['settings', 'clock-rounding'] as const;

/**
 * The per-company clock-rounding switch, on the admin Pay Periods tab.
 *
 * THE SWITCH IS NOT RENDERED UNTIL THE SETTING HAS ARRIVED. A switch drawn in
 * its off position while the GET is in flight, or after it failed, is a claim
 * that this company does not round — and a Save made from that picture would
 * turn rounding off for a company that has it on. Loading shows a spinner and
 * a failure shows what happened, in the place the switch would have been.
 *
 * STRICT admin, like every other write on this tab: `requireRole('admin',
 * { strict: true })` admits role `admin` literally, not owner and not an
 * elevated dispatcher. The GET is ungated, so anyone who reaches the tab sees
 * the setting read-only.
 *
 * WHAT IT CHANGES, which the copy has to say because it is not guessable: the
 * API snapshots `payable_minutes` onto a shift when it CLOSES, using the
 * setting in force at that moment. Flipping this never rewrites a shift that
 * has already clocked out — in either direction.
 */
export function ClockRoundingSection({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: CLOCK_ROUNDING_QUERY_KEY,
    queryFn: () => api.clockRounding.get().then((r) => unwrap<ClockRoundingSetting>(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);
  const setting = query.data;

  const mutation = useMutation({
    mutationFn: (next: 15 | null) =>
      api.clockRounding.update({ clock_rounding_minutes: next }).then((r) => unwrap<ClockRoundingSetting>(r.data)),
    onSuccess: (saved) => {
      // The server's answer, not the value we sent — it is the one that was stored.
      if (saved) setQueryDataMapped<ClockRoundingSetting>(queryClient, CLOCK_ROUNDING_QUERY_KEY, saved);
      toast.success(
        saved?.clock_rounding_minutes === 15
          ? 'Clock rounding is on. It applies from the next clock-out.'
          : 'Clock rounding is off. It applies from the next clock-out.',
      );
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Could not change clock rounding.');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CLOCK_ROUNDING_QUERY_KEY });
    },
  });

  const on = setting?.clock_rounding_minutes === 15;
  const label = 'Round clock-ins and clock-outs to the nearest 15 minutes';

  return (
    <section
      aria-labelledby="clock-rounding-heading"
      className="bg-card rounded-xl border border-border overflow-hidden"
    >
      <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
        <Timer size={18} className="text-fieldiq-accent" />
        <h2 id="clock-rounding-heading" className="text-base font-semibold text-white">Clock rounding</h2>
      </div>
      <div className="p-5">
        {failure ? (
          <LoadFailureNotice error={queryFailureError(query)} subject="the clock rounding setting" kind={failure} />
        ) : query.isLoading || !setting ? (
          <div className="flex justify-center py-4">
            <Loader2 size={20} className="animate-spin text-fieldiq-accent" aria-label="Loading clock rounding" />
          </div>
        ) : (
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-fieldiq-text">{label}</p>
              <p className="mt-1 text-xs text-fieldiq-muted">
                Applies to shifts clocked out after this change. Past shifts don't change.
              </p>
              {!canWrite && (
                <p className="mt-1 text-xs text-fieldiq-muted">Only an admin can change this.</p>
              )}
            </div>
            <Switch
              checked={on}
              aria-label={label}
              disabled={!canWrite || mutation.isPending}
              title={canWrite ? undefined : 'Only an admin can change this.'}
              onCheckedChange={(next) => mutation.mutate(next ? 15 : null)}
            />
          </div>
        )}
      </div>
    </section>
  );
}
